import React, { useRef, useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, Slider, Button } from '@mui/material';
import axios from 'axios';
import { getUploadFileUrl, createMedia } from '@api/file-upload-api.ts';
import getCroppedImg from '@utils/cropedImage.utils.ts';
import type { Area } from 'react-easy-crop';
import { ImageUploadIcon } from '@components/icons/svg-icon.tsx';
import { useTranslation } from 'react-i18next';
import { getFileCategory, getMimeType } from '@utils/media-library-utils.ts';
import AppImage from '@components/app-image/app-image.tsx';

const ImageEditor: React.FC<{
  imagePath: string | '';
  onImagePathCropped: (path: string) => void;
  isRounded?: boolean;
  cropWidth?: number;
  cropHeight?: number;
  imageSizePlaceholder?: string;
  onUploadingChange?: (uploading: boolean) => void;
  showCropPopup?: boolean;
  isPrivate?: boolean;
  localOnly?: boolean;
  width?: number | string;
  height?: number | string;
}> = ({
  imagePath,
  onImagePathCropped,
  isRounded = true,
  cropWidth = 300,
  cropHeight = 300,
  imageSizePlaceholder,
  onUploadingChange,
  showCropPopup = true,
  isPrivate = false,
  localOnly = false,
  width,
  height
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.3);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [tempImage, setTempImage] = useState<string | null>(null);
  const [fileBlob, setFileBlob] = useState<File | null>(null);
  const [croppedImagePreview, setCroppedImagePreview] = useState<string | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);

  const borderRadiusClass = isRounded ? 'rounded-full' : 'rounded-[15px]';

  const resetCropState = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(0.3);
    setTempImage(null);
    setFileBlob(null);
    setCroppedAreaPixels(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const uploadToServer = async (file: File) => {
    try {
      const { key, url } = await getUploadFileUrl({ filename: file.name });
      if (!url || !key) throw new Error('Upload URL or Key missing');

      await axios.put(url, file, {
        headers: { 'Content-Type': getMimeType(file.name) }
      });

      const media = await createMedia({
        filename: file.name,
        path: key,
        info: {
          mimetype: getMimeType(file.name),
          type: getFileCategory(getMimeType(file.name)),
          folder: 'root',
          resource: 'release',
          isPrivate,
          size: file.size
        }
      });

      onImagePathCropped(media.path);
    } catch (error) {
      void error;
      throw error;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localUrl = URL.createObjectURL(file);

    if (localOnly) {
      // Web/iframe preview: no crop, no upload — just show the picked image.
      setCroppedImagePreview(localUrl);
      onImagePathCropped(localUrl);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setTempImage(localUrl);
    setFileBlob(file);

    if (showCropPopup) {
      setCropDialogOpen(true);
    } else {
      try {
        setIsUploading(true);
        onUploadingChange?.(true);

        const image = new Image();
        image.src = localUrl;
        image.onload = async () => {
          const defaultCroppedArea: Area = {
            x: 0,
            y: 0,
            width: image.naturalWidth,
            height: image.naturalHeight
          };

          const croppedImage = await getCroppedImg(
            localUrl,
            defaultCroppedArea
          );
          setCroppedImagePreview(croppedImage);

          if (localOnly) {
            onImagePathCropped(croppedImage);
          } else {
            const response = await fetch(croppedImage);
            const blob = await response.blob();
            const croppedFile = new File([blob], file.name, {
              type: file.type
            });

            await uploadToServer(croppedFile);
          }

          resetCropState();
          setIsUploading(false);
          onUploadingChange?.(false);
        };
      } catch (error) {
        setIsUploading(false);
        onUploadingChange?.(false);
        resetCropState();
      }
    }
  };

  const onCropComplete = useCallback((_: any, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropConfirm = async () => {
    if (!fileBlob || !croppedAreaPixels) return;

    try {
      setIsUploading(true);
      onUploadingChange?.(true);

      const croppedImage = await getCroppedImg(tempImage!, croppedAreaPixels);
      setCroppedImagePreview(croppedImage);
      setCropDialogOpen(false);

      const response = await fetch(croppedImage);
      const blob = await response.blob();
      const croppedFile = new File([blob], fileBlob.name, {
        type: fileBlob.type
      });

      await uploadToServer(croppedFile);
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
      onUploadingChange?.(false);
      resetCropState();
    }
  };

  const handleCropDialogClose = () => {
    setCropDialogOpen(false);
    resetCropState();
  };

  const hasImage = croppedImagePreview || imagePath;
  const hasCustomSize = width !== undefined || height !== undefined;

  return (
    <>
      <div
        className={`media-default-trigger relative cursor-pointer ${
          hasCustomSize ? '' : 'w-43.5 h-43.5 max-lg-xl:w-35 max-lg-xl:h-35'
        }`}
        style={hasCustomSize ? { width, height } : undefined}
        onClick={() => inputRef.current?.click()}
      >
        {isUploading && (
          <svg
            className="absolute top-0 left-0 w-full h-full z-10 pointer-events-none"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {isRounded ? (
              <circle
                cx="50"
                cy="50"
                r={49.2}
                fill="none"
                stroke="var(--primary)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                strokeDashoffset="0"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="7"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            ) : (
              <rect
                x={0.75}
                y={0.75}
                width={98.5}
                height={98.5}
                rx={10.8}
                ry={10.8}
                fill="none"
                stroke="var(--primary)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                strokeDashoffset="0"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="7"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </rect>
            )}
          </svg>
        )}

        <div
          className={`flex justify-center items-center w-full h-full overflow-hidden bg-(--gray-11) z-0 transition-all duration-100 ease-in-out ${borderRadiusClass}
            ${
              isUploading
                ? ''
                : hasImage
                  ? 'border-2 border-solid border-(--primary)'
                  : 'border-2 border-dashed border-(--gray-6) hover:bg-white hover:border-(--primary)'
            }`}
        >
          {hasImage ? (
            <AppImage
              variant="image"
              filePath={localOnly ? undefined : imagePath || undefined}
              previewSrc={
                croppedImagePreview ||
                (localOnly ? imagePath : undefined) ||
                undefined
              }
              imgClassName="!object-cover !w-full !h-full"
              objectFit={'cover'}
              alt="Logo"
              fallback="Logo"
              disableModalClick
            />
          ) : (
            <div className="flex flex-col items-center gap-1">
              <ImageUploadIcon />
              <div className="flex flex-col items-center gap-0">
                <p className="text-[14px] text-(--blue-4) opacity-50">
                  {t('hotel_staff_upload_image_text')}
                </p>
                {imageSizePlaceholder && (
                  <p className="text-[14px] text-(--blue-4) opacity-50">
                    {imageSizePlaceholder}
                  </p>
                )}
              </div>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>

      <Dialog
        open={cropDialogOpen && showCropPopup}
        onClose={handleCropDialogClose}
        fullWidth
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: '680px',
              maxWidth: '100%'
            }
          }
        }}
      >
        <div className="relative h-100 bg-black">
          {tempImage && (
            <Cropper
              image={tempImage}
              crop={crop}
              cropSize={{ width: cropWidth, height: cropHeight }}
              cropShape={isRounded ? 'round' : 'rect'}
              zoom={zoom}
              aspect={cropWidth / cropHeight}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition={false}
            />
          )}
        </div>
        <div className="p-4 flex flex-col gap-4">
          <Slider
            value={zoom}
            min={0.3}
            max={3}
            step={0.1}
            onChange={(_, value) => setZoom(value as number)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outlined" onClick={handleCropDialogClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCropConfirm}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Confirm'}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ImageEditor;
